/**
 * FollowComponent - smooth camera/controller follow behavior with damping and offset support.
 */

import * as THREE from 'three';

import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { NON_SPATIAL, SceneComponent, type SceneComponentOptions } from './SceneComponent.js';

/**
 * Configuration options for the follow component
 */
export interface FollowComponentOptions extends SceneComponentOptions {
  /** The target component to follow */
  target?: SceneComponent | null;

  /** Position damping factor (0-1, where 0 is no follow, 1 is instant) */
  positionDamping?: number;

  /** Whether to use the camera's look at convention (reverse the position and look at) */
  useCameraLookAtConvention?: boolean;

  /** Offset from the target's position */
  positionOffset?: THREE.Vector3;

  /** Offset from the target's position to look at */
  lookAtOffset?: THREE.Vector3;
}

/**
 * Component that makes its owner follow another scene component
 * with configurable damping and rotation axis control
 */
@EngineClass('FollowComponent')
export class FollowComponent extends SceneComponent {
  static readonly [NON_SPATIAL] = true;

  private lastWorldPosition: THREE.Vector3 | null = null;

  @property()
  protected target: SceneComponent | null = null;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, decimals: 2, description: 'Position damping factor (0-1)' })
  protected positionDamping: number = 0.05;

  @property({ description: 'Use camera look at convention' })
  protected useCameraLookAtConvention: boolean = true;

  @property({ description: 'Offset from target position' })
  protected positionOffset: THREE.Vector3 = new THREE.Vector3(0, 5, 10);

  @property({ description: 'Offset from target position to look at' })
  protected lookAtOffset: THREE.Vector3 = new THREE.Vector3();

  public override initialize(options?: FollowComponentOptions): void {
    super.initialize(options);
    if (options?.positionDamping !== undefined) this.positionDamping = Math.max(0, Math.min(1, options.positionDamping));
    this.target = options?.target ?? this.target;
    this.useCameraLookAtConvention = options?.useCameraLookAtConvention ?? this.useCameraLookAtConvention;
    this.positionOffset = options?.positionOffset ?? this.positionOffset;
    this.lookAtOffset = options?.lookAtOffset ?? this.lookAtOffset;
  }

  public setTarget(target: SceneComponent): this {
    this.target = target;
    return this;
  }

  public getTarget(): SceneComponent | null {
    return this.target;
  }

  /**
   * @param damping - Damping factor (0-1, where 0 is no follow, 1 is instant)
   */
  public setPositionDamping(damping: number): this {
    this.positionDamping = Math.max(0, Math.min(1, damping));
    return this;
  }

  public setPositionOffset(offset: THREE.Vector3): this {
    this.positionOffset = offset;
    return this;
  }

  public setLookAtOffset(offset: THREE.Vector3): this {
    this.lookAtOffset = offset;
    return this;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    if (!this.target) {
      return;
    }

    // Handle position following with damping
    if (this.positionDamping > 0) {
      let targetPosition = this.positionOffset?.clone() ?? new THREE.Vector3();
      this.target.localToWorld(targetPosition);

      this.lastWorldPosition ??= targetPosition.clone();

      // Interpolate between current and target position based on damping
      this.lastWorldPosition.lerp(targetPosition, this.positionDamping);

      // Update position
      this.setWorldPosition(this.lastWorldPosition);

      let lookAtPosition = this.lookAtOffset?.clone() ?? new THREE.Vector3();
      this.target.localToWorld(lookAtPosition);
      if (this.useCameraLookAtConvention) {
        this.lookAt(this.lastWorldPosition.clone().multiplyScalar(2).sub(lookAtPosition));
      } else {
        this.lookAt(lookAtPosition);
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Follow';
  }
}
